import React, { useEffect, useState } from "react";
import { Input, Flex, List, Modal } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import CountriesModal from "../../../../common/CountriesModal";
import CustomLoader from "../../../../common/UI/Loader";
import ENDPOINTS from "../../../../../services/endpoint";
import apiInstance from "../../../../../services/apiService";
import AddonCard from "../../../../common/AddonCard";
import SettingsSidebarHeader from "../../../../common/SettingsSidebarHeader";
import "./index.scss";

interface CallAddonsProps {
  handleBackToPrevious: () => void;
}

interface Country {
  countryCode: number;
  countryName: string;
  isoCode: string;
  iso3Code: string;
}

interface SelectedCountry {
  code: string;
  dial: string;
  name: string;
  flag: string;
}

interface Addon {
  _id: string;
  addonName: string;
  addonCost: string;
  addonType: number;
  type: string;
  countries: Country[];
}

const CallingAddons: React.FC<CallAddonsProps> = ({ handleBackToPrevious }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [addonData, setAddonData] = useState<Addon[] | null>(null);
  const [countrySelectorModalVisible, setCountrySelectorModalVisible] =
    useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] =
    useState<SelectedCountry | null>(null);
  const [filteredAddons, setFilteredAddons] = useState<Addon[] | null>(null);
  const [paymentGatewayModalOpen, setPaymentGatewayModalOpen] =
    useState<boolean>(false);
  const [paymentGatewayDetails, setPaymentGatewayDetails] = useState<any>(null);

  const getAddons = async () => {
    const url = ENDPOINTS.ADDONS;

    setLoading(true);
    try {
      const response = await apiInstance.get(url);
      const data = response.data.availAddons;
      setAddonData(data);
    } catch (error: any) {
      console.error(error);
      toast.error(error.msg || "There was an error fetching addons");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (item: Addon) => {
    const url = ENDPOINTS.CANCEL_CALL;

    setLoading(true);
    try {
      const response = await apiInstance.post(url, {
        addonIds: [item._id],
      });
      const message = response.data.message;
      toast.success(message || "subscribed successfulyy");
      getAddons();
    } catch (error: any) {
      if (error.status === 400 && error?.response?.data?.hostedpageDetails) {
        toast.info("First add  card from subscription page  for purchasing.");
        setPaymentGatewayDetails(error?.response.data.hostedpageDetails);
        setPaymentGatewayModalOpen(true);
      } else {
        toast.error(error.response.data.message || "Failed to subscribe");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAddons();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const filtered = addonData
        ? addonData.filter((addon) =>
            addon.countries.some((country) =>
              country.countryName
                .toLowerCase()
                .includes(selectedCountry.name.toLowerCase())
            )
          )
        : [];

      setFilteredAddons(filtered);
    } else {
      setFilteredAddons(addonData);
    }
  }, [selectedCountry]);

  return (
    <div className="calling-addons-container">
      <SettingsSidebarHeader
        title="Calling Addons"
        handleBackClick={handleBackToPrevious}
      />

      <div className="main-section">
        <Input
          placeholder="Search country..."
          readOnly
          prefix={<SearchOutlined />}
          className="search-input"
          value={selectedCountry?.name || ""}
          onClick={() => setCountrySelectorModalVisible(true)}
          allowClear
          onClear={() => setSelectedCountry(null)}
        />

        <List
          key="filtered-addons"
          className="filtered-addon-list"
          dataSource={filteredAddons || addonData || []}
          loading={{
            spinning: loading,
            indicator: (
              <Flex className="h-full w-full" justify="center" align="center">
                <CustomLoader />
              </Flex>
            ),
          }}
          renderItem={(addon: any) => (
            <List.Item key={addon._id}>
              <AddonCard addon={addon} onSubscribe={handleSubscribe} />
            </List.Item>
          )}
        />
      </div>

      <CountriesModal
        visible={countrySelectorModalVisible}
        onClose={() => setCountrySelectorModalVisible(false)}
        onSelect={(country: any) => {
          setSelectedCountry(country);
          setCountrySelectorModalVisible(false);
        }}
      />

      <Modal
        open={paymentGatewayModalOpen}
        onCancel={() => setPaymentGatewayModalOpen(false)}
        footer={null}
        className="custom-modal"
      >
        <iframe
          title="payment gateway"
          src={paymentGatewayDetails?.url}
          width="100%"
          height="660px"
          style={{ border: "none" }}
          allowFullScreen
        />
      </Modal>
    </div>
  );
};

export default CallingAddons;
